import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button, ClipSpinner } from '~/components/ui';
import { ContactCard } from '~/components/cards/ContactCard';
import {
  computeAllSelected,
  useContactSelection,
} from '~/hooks/useContactSelection';
import { AddSellerContactPanel } from '~/components/common/AddSellerContactPanel';
import type {
  Contact,
  ContactDetails,
  ContactsStatus,
  SavedContact,
} from '~/utils/contact';

type PrimaryContactModeProps =
  | Readonly<{
      primaryContactMode: 'required';
      primaryRequiredMessage: string;
      onConfirm: (
        selectedContactIds: string[],
        primaryContact: Contact
      ) => void;
    }>
  | Readonly<{
      primaryContactMode: 'readOnly';
      primaryRequiredMessage?: never;
      onConfirm: (selectedContactIds: string[]) => void;
    }>;

export type SelectContactsBodyProps = Readonly<{
  onClose: () => void;
  organizationId: string;
  contacts: ContactDetails[];
  initialContactIds: string[];
  lockedContactIds?: string[];
  initialPrimaryContactId?: string;
  openAddFormWhenEmpty?: boolean;
  lockedLabel?: string;
  isSubmitting?: boolean;
  confirmLabel?: string;
  selectionRequired?: boolean;
  contactNote?: (contact: ContactDetails) => string | undefined;
  noContactsMessage?: string;
}> &
  PrimaryContactModeProps;

type SelectContactsModalProps = SelectContactsBodyProps &
  Readonly<{
    isOpen: boolean;
    title: string;
    subtitle: string;
    contactsStatus?: ContactsStatus;
  }>;

const noContactNote = () => undefined;

export function SelectContactsBody(props: SelectContactsBodyProps) {
  const {
    onClose,
    organizationId,
    contacts,
    initialContactIds,
    lockedContactIds = [],
    initialPrimaryContactId,
    openAddFormWhenEmpty = false,
    lockedLabel,
    primaryContactMode,
    primaryRequiredMessage,
    isSubmitting = false,
    confirmLabel,
    selectionRequired = false,
    contactNote = noContactNote,
    noContactsMessage,
  } = props;
  const { t } = useTranslation('general');
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const [primaryContactId, setPrimaryContactId] = useState<string | undefined>(
    initialPrimaryContactId
  );
  const lockedIds = new Set(
    lockedContactIds.filter((id) =>
      contacts.some((contact) => contact.id === id)
    )
  );
  const { selectedContactIds, toggleContact, toggleSelectAll } =
    useContactSelection(
      contacts.filter((contact) => !lockedIds.has(contact.id)),
      initialContactIds
    );
  const primaryEditable = primaryContactMode === 'required';
  const listedPrimaryContact = contacts.find(
    (contact) => contact.id === primaryContactId
  );
  const isPrimaryContactListed = listedPrimaryContact !== undefined;
  const notifiedContactIds = new Set([...selectedContactIds, ...lockedIds]);
  if (primaryEditable && isPrimaryContactListed) {
    notifiedContactIds.add(listedPrimaryContact.id);
  }
  const allSelected = computeAllSelected(contacts, notifiedContactIds);
  const [startsWithForm] = useState(
    openAddFormWhenEmpty && contacts.length === 0
  );

  const primaryMissing = primaryEditable && !isPrimaryContactListed;
  const selectionMissing = selectionRequired && notifiedContactIds.size === 0;

  const handleContactAdded = (contact: SavedContact) => {
    if (primaryMissing) {
      setPrimaryContactId(contact.id);
    } else {
      toggleContact(contact.id);
    }
  };

  const isPrimary = (contactId: string) =>
    contactId ===
    (primaryEditable ? primaryContactId : initialPrimaryContactId);

  const emptyMessage =
    noContactsMessage ?? t('select_contacts_modal.no_contacts');

  const renderContactList = (addContactTrigger: ReactNode) =>
    contacts.length === 0 ? (
      <>
        <p className="text-sm text-neut-sec">{emptyMessage}</p>
        <div>{addContactTrigger}</div>
      </>
    ) : (
      <>
        <div className="px-1 pb-2 flex items-center gap-2 border-b border-neut-border">
          <input
            id="select-contacts-all"
            type="checkbox"
            className="h-4 w-4 cursor-pointer"
            checked={allSelected}
            onChange={() => toggleSelectAll(allSelected)}
          />
          <label
            htmlFor="select-contacts-all"
            className="text-sm font-medium w-full cursor-pointer"
          >
            {t('select_contacts_modal.select_all')}
          </label>
        </div>
        <ul className="mt-3 grid auto-rows-fr gap-2">
          {contacts.map((contact) => (
            <li key={contact.id}>
              <ContactCard
                contact={contact}
                selected={notifiedContactIds.has(contact.id)}
                onSelectedChange={() => toggleContact(contact.id)}
                selectDisabled={
                  (primaryEditable && contact.id === primaryContactId) ||
                  lockedIds.has(contact.id)
                }
                actedLabel={
                  lockedIds.has(contact.id) ? lockedLabel : contactNote(contact)
                }
                primary={isPrimary(contact.id)}
                onPrimaryChange={
                  primaryEditable
                    ? (primary) =>
                        setPrimaryContactId(primary ? contact.id : undefined)
                    : undefined
                }
              />
            </li>
          ))}
        </ul>
        <div className="mt-3">{addContactTrigger}</div>
        {primaryMissing && (
          <p className="text-sm text-neut-sec mt-3">{primaryRequiredMessage}</p>
        )}
      </>
    );

  const handleConfirm = () => {
    if (selectionMissing) {
      return;
    }
    const contactIds = Array.from(notifiedContactIds);
    if (props.primaryContactMode !== 'required') {
      props.onConfirm(contactIds);
      return;
    }
    if (listedPrimaryContact) {
      props.onConfirm(contactIds, listedPrimaryContact);
    }
  };

  return (
    <AddSellerContactPanel
      organizationId={organizationId}
      testId="add-contact-button"
      disabled={isSubmitting}
      initiallyOpen={startsWithForm}
      focusAfterSave={startsWithForm ? confirmButtonRef : undefined}
      onAdded={handleContactAdded}
      onCancel={startsWithForm ? onClose : undefined}
    >
      {({ trigger, form }) => (
        <>
          <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-3">
            {form && startsWithForm && (
              <p className="text-sm text-neut-sec">{emptyMessage}</p>
            )}
            {form ?? renderContactList(trigger)}
          </div>
          {!form && (
            <div className="px-6 py-4 border-t border-neut-border flex flex-wrap items-center gap-3 shrink-0">
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {t('select_contacts_modal.cancel')}
              </Button>
              <Button
                ref={confirmButtonRef}
                variant="primary"
                onClick={handleConfirm}
                loading={isSubmitting}
                disabled={isSubmitting || primaryMissing || selectionMissing}
                data-testid="confirm-contacts-button"
              >
                {confirmLabel ?? t('select_contacts_modal.confirm')}
              </Button>
            </div>
          )}
        </>
      )}
    </AddSellerContactPanel>
  );
}

export function SelectContactsModal({
  isOpen,
  onClose,
  title,
  subtitle,
  contactsStatus = 'loaded',
  isSubmitting = false,
  ...selectionProps
}: SelectContactsModalProps) {
  const { t } = useTranslation('general');

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const renderBody = () => {
    if (contactsStatus === 'loading') {
      return (
        <div className="px-6 py-4">
          <ClipSpinner />
        </div>
      );
    }
    if (contactsStatus === 'failed') {
      return (
        <p className="px-6 py-4 text-sm text-error-600">
          {t('select_contacts_modal.load_failed')}
        </p>
      );
    }
    return (
      <SelectContactsBody
        {...selectionProps}
        onClose={handleClose}
        isSubmitting={isSubmitting}
      />
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div
        className="bg-neut-light rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
        data-testid="select-contacts-modal"
      >
        <ModalHeader
          title={title}
          description={subtitle}
          onClose={handleClose}
          closeDisabled={isSubmitting}
        />
        {renderBody()}
      </div>
    </Modal>
  );
}
